import React, { useEffect, useRef, useState } from 'react';
import { GamePhase } from '../types';
import cheersSprite from '../assets/optimized_cheers_spritesheet.png';
import { useShellLayout } from '../hooks/useShellLayout';
import { getMugOverlayStyle } from './mugOverlay';

interface CheersAnimationProps {
    phase: GamePhase;
    playKey?: number;  // When provided and changes, triggers animation (command-based mode)
    onPlayingChange?: (isPlaying: boolean) => void;
    /** Called 1 frame before animation ends (for StaticMug sync) */
    onFrameNearEnd?: () => void;
}

const FRAME_WIDTH = 480;
const FRAME_HEIGHT = 847;
const COLS = 11;
const TOTAL_FRAMES = 108;
const FPS = 30; // 30fps seems reasonable for smooth animation

const CheersAnimation: React.FC<CheersAnimationProps> = ({ phase, playKey, onPlayingChange, onFrameNearEnd }) => {
    const { isMobileShell } = useShellLayout();
    const [isPlaying, setIsPlaying] = useState(false);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const frameRef = useRef(0);
    const lastPhaseRef = useRef<GamePhase>(phase);
    const lastPlayKeyRef = useRef<number | undefined>(playKey);
    const spriteRef = useRef<HTMLImageElement | null>(null);
    const nearEndFiredRef = useRef(false);

    // Notify parent about playing state changes
    useEffect(() => {
        onPlayingChange?.(isPlaying);
    }, [isPlaying, onPlayingChange]);

    // Load sprite
    useEffect(() => {
        const img = new Image();
        img.src = cheersSprite;
        img.onload = () => {
            spriteRef.current = img;
        };
    }, []);

    // Trigger animation on phase change (Day <-> Night) OR on playKey change (command-based mode)
    useEffect(() => {
        const prev = lastPhaseRef.current;
        const prevPlayKey = lastPlayKeyRef.current;

        // Command-based mode: trigger on playKey change
        if (playKey !== undefined && prevPlayKey !== undefined && playKey !== prevPlayKey) {
            if (spriteRef.current) {
                console.log('[CheersAnimation] playKey changed:', prevPlayKey, '->', playKey, '- Triggering animation!');
                setIsPlaying(true);
                frameRef.current = 0;
                nearEndFiredRef.current = false;
            }
            lastPlayKeyRef.current = playKey;
            lastPhaseRef.current = phase;
            return;
        }
        lastPlayKeyRef.current = playKey;

        // Legacy phase-based mode (when playKey is not being used)
        if (playKey === undefined) {
            // Night phases (where we consider ourselves "in Night")
            const isNightPhase = (p: GamePhase) => p === GamePhase.Combat || p === GamePhase.TowerPurchase;

            // Day -> Night: Entering Combat from any non-Night phase
            const dayToNight = !isNightPhase(prev) && phase === GamePhase.Combat;

            // Night -> Day: Entering Preparation from any phase
            const nightToDay = phase === GamePhase.Preparation && prev !== GamePhase.Preparation;

            console.log('[CheersAnimation] Phase change:', prev, '->', phase, '| dayToNight:', dayToNight, '| nightToDay:', nightToDay);

            if (dayToNight || nightToDay) {
                if (spriteRef.current) {
                    console.log('[CheersAnimation] Triggering animation!');
                    setIsPlaying(true);
                    frameRef.current = 0;
                    nearEndFiredRef.current = false;
                }
            }
        }

        lastPhaseRef.current = phase;
    }, [phase, playKey]);

    // Animation Loop
    useEffect(() => {
        if (!isPlaying || !spriteRef.current || !canvasRef.current) return;

        let animationId: number;
        let lastTime = 0;
        const interval = 1000 / FPS;

        const render = (time: number) => {
            if (time - lastTime > interval) {
                lastTime = time;
                const ctx = canvasRef.current?.getContext('2d');
                if (ctx && spriteRef.current) {
                    const frame = frameRef.current;

                    // Fire near-end callback 1 frame before completion
                    if (frame === TOTAL_FRAMES - 1 && !nearEndFiredRef.current) {
                        nearEndFiredRef.current = true;
                        onFrameNearEnd?.();
                    }

                    if (frame >= TOTAL_FRAMES) {
                        setIsPlaying(false);
                        ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
                        return;
                    }

                    const col = frame % COLS;
                    const row = Math.floor(frame / COLS);
                    const sx = col * FRAME_WIDTH;
                    const sy = row * FRAME_HEIGHT;

                    // Clear previous
                    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);

                    // Draw
                    ctx.drawImage(
                        spriteRef.current,
                        sx, sy, FRAME_WIDTH, FRAME_HEIGHT,
                        0, 0, ctx.canvas.width, ctx.canvas.height
                    );

                    frameRef.current++;
                }
            }
            animationId = requestAnimationFrame(render);
        };

        animationId = requestAnimationFrame(render);

        return () => cancelAnimationFrame(animationId);
    }, [isPlaying, onFrameNearEnd]);

    if (!isPlaying) return null;

    return (
        <canvas
            ref={canvasRef}
            width={FRAME_WIDTH}
            height={FRAME_HEIGHT}
            className="fixed z-40 pointer-events-none"
            style={getMugOverlayStyle(isMobileShell)}
        />
    );
};

export default CheersAnimation;
